import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { FILTER_TYPES, DATA_PROPERTIES } from '../utils/constants';

const FILTER_STORAGE_KEY = 'flowmapFilters';

export const useFlowmapFilters = (flowmapData) => {
    const router = useRouter();
    const searchParams = useSearchParams();

    const hasInitializedFromData = useRef(false);

    const highestValue = useMemo(() => {
        if (!flowmapData?.data || flowmapData.data.length === 0) {
            return 100;
        }
        return Math.max(...flowmapData.data.map((item) => item.value));
    }, [flowmapData]);

    const yearData = useMemo(() => {
        if (!flowmapData?.data || flowmapData.data.length === 0) {
            const currentYear = new Date().getFullYear();
            return {
                min: currentYear,
                max: currentYear,
                availableYears: [currentYear],
            };
        }
        const years = flowmapData.data
            .map((item) => item.year)
            .filter((year) => year != null && !isNaN(year))
            .map((year) => parseInt(year, 10));

        if (years.length === 0) {
            const currentYear = new Date().getFullYear();
            return {
                min: currentYear,
                max: currentYear,
                availableYears: [currentYear],
            };
        }

        const uniqueYears = [...new Set(years)].sort((a, b) => a - b);

        return {
            min: Math.min(...years),
            max: Math.max(...years),
            availableYears: uniqueYears,
        };
    }, [flowmapData]);

    useEffect(() => {
        if (!searchParams) return;
        const urlFilters = parseQueryFilters(searchParams);
        setFilters((prev) => ({
            ...prev,
            ...urlFilters,
        }));
    }, [searchParams]);

    const parseQueryFilters = (searchParams) => {
        const parsed = {};

        const getArray = (key) => {
            const value = searchParams.get(key);
            return value ? value.split(',') : [];
        };

        const getNumber = (key, fallback = null) => {
            const value = searchParams.get(key);
            return value ? parseInt(value, 10) : fallback;
        };

        parsed[FILTER_TYPES.FROM_COUNTRIES] = getArray('fromCountries');
        parsed[FILTER_TYPES.TO_COUNTRIES] = getArray('toCountries');
        parsed[FILTER_TYPES.SOURCES] = getArray('sources');
        parsed[FILTER_TYPES.TYPES] = getArray('types');
        parsed[FILTER_TYPES.HS_CODES] = getArray('hsCodes');
        parsed[FILTER_TYPES.UNITS] = getArray('units');
        parsed[FILTER_TYPES.YEAR] = getNumber('year', null);

        return parsed;
    };

    const getInitialFilters = () => {
        if (typeof window !== 'undefined') {
            const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    delete parsed[FILTER_TYPES.VALUE_MIN];
                    delete parsed[FILTER_TYPES.VALUE_MAX];
                    return {
                        [FILTER_TYPES.FROM_COUNTRIES]: [],
                        [FILTER_TYPES.TO_COUNTRIES]: [],
                        [FILTER_TYPES.SOURCES]: [],
                        [FILTER_TYPES.TYPES]: [],
                        [FILTER_TYPES.HS_CODES]: [],
                        [FILTER_TYPES.UNITS]: [],
                        [FILTER_TYPES.YEAR]: null,
                        ...parsed,
                    };
                } catch (e) {
                    // Ignore corrupt sessionStorage
                }
            }
        }

        const urlFilters = parseQueryFilters(searchParams);

        return {
            [FILTER_TYPES.FROM_COUNTRIES]: [],
            [FILTER_TYPES.TO_COUNTRIES]: [],
            [FILTER_TYPES.SOURCES]: [],
            [FILTER_TYPES.TYPES]: [],
            [FILTER_TYPES.HS_CODES]: [],
            [FILTER_TYPES.UNITS]: [],
            [FILTER_TYPES.YEAR]: urlFilters[FILTER_TYPES.YEAR],
            ...urlFilters,
        };
    };

    const [filters, setFilters] = useState(getInitialFilters);

    useEffect(() => {
        if (!flowmapData?.data || hasInitializedFromData.current) return;

        hasInitializedFromData.current = true;

        const urlYear = searchParams.get('year');
        const dataYears = flowmapData.data
            .map((item) => item.year)
            .filter((year) => year != null && !isNaN(year))
            .map((year) => parseInt(year, 10));

        if (dataYears.length === 0) return;

        let targetYear = null;

        // Only set year if explicitly provided in URL
        if (urlYear) {
            const urlYearNum = parseInt(urlYear, 10);
            if (dataYears.includes(urlYearNum)) {
                targetYear = urlYearNum;
            }
        }

        if (targetYear && targetYear !== filters[FILTER_TYPES.YEAR]) {
            setFilters((prev) => ({
                ...prev,
                [FILTER_TYPES.YEAR]: targetYear,
            }));
        }
    }, [flowmapData]);

    const filteredData = useMemo(() => {
        if (!flowmapData?.data) {
            return null;
        }

        // Check if any meaningful filters are active
        const hasActiveFilters =
            (filters[FILTER_TYPES.FROM_COUNTRIES] &&
                filters[FILTER_TYPES.FROM_COUNTRIES].length > 0) ||
            (filters[FILTER_TYPES.TO_COUNTRIES] &&
                filters[FILTER_TYPES.TO_COUNTRIES].length > 0) ||
            (filters[FILTER_TYPES.SOURCES] &&
                filters[FILTER_TYPES.SOURCES].length > 0) ||
            (filters[FILTER_TYPES.TYPES] &&
                filters[FILTER_TYPES.TYPES].length > 0) ||
            (filters[FILTER_TYPES.HS_CODES] &&
                filters[FILTER_TYPES.HS_CODES].length > 0) ||
            (filters[FILTER_TYPES.UNITS] &&
                filters[FILTER_TYPES.UNITS].length > 0) ||
            (filters[FILTER_TYPES.YEAR] !== null &&
                filters[FILTER_TYPES.YEAR] !== undefined);

        // If no filters are active, return empty data
        if (!hasActiveFilters) {
            return {
                ...flowmapData,
                data: [],
            };
        }

        try {
            let filteredItems = flowmapData.data.filter((item) => {
                return (
                    filters[FILTER_TYPES.YEAR] === null ||
                    filters[FILTER_TYPES.YEAR] === undefined ||
                    item.year == null ||
                    parseInt(item.year, 10) === filters[FILTER_TYPES.YEAR]
                );
            });

            const filterConditions = [
                {
                    filterArray: filters[FILTER_TYPES.FROM_COUNTRIES],
                    itemProperty: DATA_PROPERTIES.START_COUNTRY_ISO,
                },
                {
                    filterArray: filters[FILTER_TYPES.TO_COUNTRIES],
                    itemProperty: DATA_PROPERTIES.END_COUNTRY_ISO,
                },
                {
                    filterArray: filters[FILTER_TYPES.SOURCES],
                    itemProperty: DATA_PROPERTIES.DATA_SOURCE,
                },
                {
                    filterArray: filters[FILTER_TYPES.TYPES],
                    itemProperty: DATA_PROPERTIES.DATA_TYPE,
                },
                {
                    filterArray: filters[FILTER_TYPES.HS_CODES],
                    itemProperty: DATA_PROPERTIES.HS_CODE,
                },
                {
                    filterArray: filters[FILTER_TYPES.UNITS],
                    itemProperty: DATA_PROPERTIES.UNIT,
                },
            ];

            filterConditions.forEach(({ filterArray, itemProperty }) => {
                if (Array.isArray(filterArray) && filterArray.length > 0) {
                    filteredItems = filteredItems.filter((item) =>
                        filterArray.includes(item[itemProperty])
                    );
                }
            });

            return {
                ...flowmapData,
                data: filteredItems,
            };
        } catch (error) {
            console.error('Error filtering flowmap data:', error);
            return flowmapData;
        }
    }, [flowmapData, filters]);

    // Single memo for all filter types — recalculates all options on any filter change.
    // Acceptable for datasets <10k items; split into per-filter memos if perf becomes an issue.
    const availableOptions = useMemo(() => {
        if (!flowmapData?.data) {
            return {
                [FILTER_TYPES.FROM_COUNTRIES]: new Set(),
                [FILTER_TYPES.TO_COUNTRIES]: new Set(),
                [FILTER_TYPES.SOURCES]: new Set(),
                [FILTER_TYPES.TYPES]: new Set(),
                [FILTER_TYPES.HS_CODES]: new Set(),
                [FILTER_TYPES.UNITS]: new Set(),
            };
        }

        const getFilteredDataExcluding = (excludeFilter) => {
            let items = flowmapData.data.filter((item) => {
                return (
                    filters[FILTER_TYPES.YEAR] === null ||
                    filters[FILTER_TYPES.YEAR] === undefined ||
                    item.year == null ||
                    parseInt(item.year, 10) === filters[FILTER_TYPES.YEAR]
                );
            });

            const filterConditions = [
                {
                    filterType: FILTER_TYPES.FROM_COUNTRIES,
                    filterArray: filters[FILTER_TYPES.FROM_COUNTRIES],
                    itemProperty: DATA_PROPERTIES.START_COUNTRY_ISO,
                },
                {
                    filterType: FILTER_TYPES.TO_COUNTRIES,
                    filterArray: filters[FILTER_TYPES.TO_COUNTRIES],
                    itemProperty: DATA_PROPERTIES.END_COUNTRY_ISO,
                },
                {
                    filterType: FILTER_TYPES.SOURCES,
                    filterArray: filters[FILTER_TYPES.SOURCES],
                    itemProperty: DATA_PROPERTIES.DATA_SOURCE,
                },
                {
                    filterType: FILTER_TYPES.TYPES,
                    filterArray: filters[FILTER_TYPES.TYPES],
                    itemProperty: DATA_PROPERTIES.DATA_TYPE,
                },
                {
                    filterType: FILTER_TYPES.HS_CODES,
                    filterArray: filters[FILTER_TYPES.HS_CODES],
                    itemProperty: DATA_PROPERTIES.HS_CODE,
                },
                {
                    filterType: FILTER_TYPES.UNITS,
                    filterArray: filters[FILTER_TYPES.UNITS],
                    itemProperty: DATA_PROPERTIES.UNIT,
                },
            ];

            filterConditions.forEach(
                ({ filterType, filterArray, itemProperty }) => {
                    if (
                        filterType !== excludeFilter &&
                        Array.isArray(filterArray) &&
                        filterArray.length > 0
                    ) {
                        items = items.filter((item) =>
                            filterArray.includes(item[itemProperty])
                        );
                    }
                }
            );

            return items;
        };

        const available = {};

        [
            FILTER_TYPES.FROM_COUNTRIES,
            FILTER_TYPES.TO_COUNTRIES,
            FILTER_TYPES.SOURCES,
            FILTER_TYPES.TYPES,
            FILTER_TYPES.HS_CODES,
            FILTER_TYPES.UNITS,
        ].forEach((filterType) => {
            const dataForThisFilter = getFilteredDataExcluding(filterType);
            const propertyMap = {
                [FILTER_TYPES.FROM_COUNTRIES]:
                    DATA_PROPERTIES.START_COUNTRY_ISO,
                [FILTER_TYPES.TO_COUNTRIES]: DATA_PROPERTIES.END_COUNTRY_ISO,
                [FILTER_TYPES.SOURCES]: DATA_PROPERTIES.DATA_SOURCE,
                [FILTER_TYPES.TYPES]: DATA_PROPERTIES.DATA_TYPE,
                [FILTER_TYPES.HS_CODES]: DATA_PROPERTIES.HS_CODE,
                [FILTER_TYPES.UNITS]: DATA_PROPERTIES.UNIT,
            };

            available[filterType] = new Set(
                dataForThisFilter
                    .map((item) => item[propertyMap[filterType]])
                    .filter(Boolean)
            );
        });

        // Special logic for year-category dependency
        available.availableYears = new Set();
        available.availableCategories = new Set();

        // Get available years based on selected category
        const getDataFilteredByCategory = () => {
            let items = flowmapData.data;

            if (
                filters[FILTER_TYPES.TYPES] &&
                filters[FILTER_TYPES.TYPES].length > 0
            ) {
                items = items.filter((item) =>
                    filters[FILTER_TYPES.TYPES].includes(
                        item[DATA_PROPERTIES.DATA_TYPE]
                    )
                );
            }

            return items;
        };

        // Get available categories based on selected year
        const getDataFilteredByYear = () => {
            let items = flowmapData.data;

            if (
                filters[FILTER_TYPES.YEAR] !== null &&
                filters[FILTER_TYPES.YEAR] !== undefined
            ) {
                items = items.filter(
                    (item) =>
                        item.year == null ||
                        parseInt(item.year, 10) === filters[FILTER_TYPES.YEAR]
                );
            }

            return items;
        };

        const dataFilteredByCategory = getDataFilteredByCategory();
        const dataFilteredByYear = getDataFilteredByYear();

        // Available years based on category selection
        available.availableYears = new Set(
            dataFilteredByCategory
                .map((item) => item.year)
                .filter((year) => year != null && !isNaN(year))
                .map((year) => parseInt(year, 10))
        );

        // Available categories based on year selection
        available.availableCategories = new Set(
            dataFilteredByYear
                .map((item) => item[DATA_PROPERTIES.DATA_TYPE])
                .filter(Boolean)
        );

        return available;
    }, [flowmapData, filters]);

    useEffect(() => {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
                params.set(key, value.join(','));
            } else if (value !== undefined && value !== null && value !== '') {
                params.set(key, value.toString());
            } else {
                params.delete(key);
            }
        });

        params.delete('valueMin');
        params.delete('valueMax');

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        router.replace(newUrl, { scroll: false });

        try {
            sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
        } catch (e) {
            // Ignore sessionStorage write failure
        }
    }, [filters, router]);

    const updateFilter = useCallback((filterName, value) => {
        setFilters((prev) => {
            const newValue =
                typeof value === 'function' ? value(prev[filterName]) : value;
            return {
                ...prev,
                [filterName]: newValue,
            };
        });
    }, []);

    const resetFilters = useCallback(() => {
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
        setFilters({
            [FILTER_TYPES.FROM_COUNTRIES]: [],
            [FILTER_TYPES.TO_COUNTRIES]: [],
            [FILTER_TYPES.SOURCES]: [],
            [FILTER_TYPES.TYPES]: [],
            [FILTER_TYPES.HS_CODES]: [],
            [FILTER_TYPES.UNITS]: [],
            [FILTER_TYPES.YEAR]: null,
        });
    }, []);

    const activeFilters = useMemo(() => {
        let count = 0;

        const arrayFilters = [
            FILTER_TYPES.FROM_COUNTRIES,
            FILTER_TYPES.TO_COUNTRIES,
            FILTER_TYPES.SOURCES,
            FILTER_TYPES.TYPES,
            FILTER_TYPES.HS_CODES,
            FILTER_TYPES.UNITS,
        ];

        arrayFilters.forEach((type) => {
            count += filters[type]?.length || 0;
        });

        if (
            filters[FILTER_TYPES.YEAR] !== null &&
            filters[FILTER_TYPES.YEAR] !== undefined
        ) {
            count += 1;
        }

        return count;
    }, [filters]);

    return {
        filters,
        filteredData,
        updateFilter,
        resetFilters,
        highestValue,
        yearRange: yearData,
        availableOptions,
        activeFilters,
    };
};
